'use client';

import { c as _c } from "react/compiler-runtime";
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FieldValues } from 'react-hook-form';
import { useCart } from '@sola/cart-sdk/store.tsx';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { PaymentMethodMutationResponse } from '@sola/schema/dotcom/proxy-api/paymentMethods.ts';
import type { GraphQLCommitment, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isItemPayment } from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';
import { MUTATION_KEYS, QUERY_KEYS } from '@sola/supporter-sdk/constants.ts';
import { useReadyForQueries } from '@sola/supporter-sdk/hooks/base.ts';
import type { CommitmentPaymentPlanUpdateRes } from './requests.ts';
import { updateCommitmentPaymentPlan } from './requests.ts';
export type CommitmentCartPaymentItem = Extract<CartItem, {
  itemType: CartItemTypeEnum.Payment;
}>;
export interface CommitmentAddVariables {
  payments: FieldValues;
}

/**
 * Update the commitment cache with the payment plan results.
 */
export const updateCommitmentCache = (queryClient: QueryClient, commitmentUpdates?: PaymentMethodMutationResponse['commitmentUpdates'], supporterGlobalId?: string) => {
  if (!commitmentUpdates?.updated.length) {
    return;
  }
  queryClient.setQueryData([QUERY_KEYS.supporterCommitments, supporterGlobalId], (currentCommitments: GraphQLCommitment[] = []) => {
    // Create lookup so we don't have to run the loop every time.
    const updatedById: Record<string, GraphQLCommitment> = {};
    for (const commitment of commitmentUpdates.updated) {
      if (!commitment?.internalId) {
        continue;
      }
      updatedById[commitment.internalId] = commitment;
    }
    return currentCommitments.map(commitment => updatedById[commitment.internalId] || commitment);
  });
};

/**
 * Hook to update a commitment payment plan.
 */
export const useCommitmentPaymentPlanUpdate = commitmentId => {
  const $ = _c(12);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  let t0;
  if ($[0] !== commitmentId) {
    t0 = [MUTATION_KEYS.commitmentPaymentPlanUpdate, commitmentId];
    $[0] = commitmentId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== commitmentId || $[3] !== globalId) {
    t1 = t2 => {
      const {
        newPaymentMethodId,
        paymentDay
      } = t2;
      return updateCommitmentPaymentPlan(globalId, commitmentId, newPaymentMethodId, paymentDay);
    };
    $[2] = commitmentId;
    $[3] = globalId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== globalId || $[6] !== queryClient) {
    t2 = res => {
      if (!res?.success) {
        return;
      }
      updateCommitmentCache(queryClient, res.commitmentUpdates, globalId);
    };
    $[5] = globalId;
    $[6] = queryClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      mutationKey: t0,
      mutationFn: t1,
      onSuccess: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useMutation(t3);
};

/**
 * Hook to add or remove commitment payments from the cart and continue to checkout.
 */
export const useCommitmentPaymentsAdd = () => {
  const $ = _c(10);
  const {
    actions,
    cart,
    itemsBusy
  } = useCart("actions", "cart", "itemsBusy");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [MUTATION_KEYS.makeAPaymentAdd];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== actions) {
    t1 = () => {
      actions.goToCheckout();
    };
    $[1] = actions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== actions || $[4] !== cart?.items || $[5] !== itemsBusy?.length) {
    t2 = async t3 => {
      const {
        payments
      } = t3;
      if (itemsBusy?.length) {
        throw new Error("Invalid property or items already being added to cart.");
      }
      const currentCartItems = cart?.items.map(_temp);
      const paymentEntries = Object.entries(payments).filter(t4 => {
        const [commitmentId, amount] = t4;
        return typeof amount === "number" && amount >= 0 || currentCartItems?.includes(commitmentId);
      });
      const commitmentItems = paymentEntries.map(_temp2);
      if (commitmentItems.length > 0) {
        await actions.updateCommitmentItems(commitmentItems);
      }
    };
    $[3] = actions;
    $[4] = cart?.items;
    $[5] = itemsBusy?.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationKey: t0,
      onSuccess: t1,
      mutationFn: t2,
      onError: _temp3
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useMutation(t3);
};
function _temp(item) {
  return isItemPayment(item) && item.payment.commitmentId;
}
function _temp2(t0) {
  const [commitmentId_0, amount_0] = t0;
  return {
    commitmentId: commitmentId_0,
    unitPrice: typeof amount_0 === "number" && amount_0 >= 0 ? amount_0 : 0
  };
}
function _temp3() {
  throw new Error("Something went wrong with adding items to the cart.");
}